import styled from '@emotion/styled';
import { useState } from 'react';
import { X as XIcon, Frown, MousePointerClick, Wallet, PackageX } from 'lucide-react';

const CANCELLATION_REASONS = [
  {
    id: 'unhappy',
    label: 'Unhappy with the product, quality, or fit',
    icon: Frown,
  },
  {
    id: 'prefer-on-demand',
    label: 'Prefer to order when I need it',
    icon: MousePointerClick,
  },
  {
    id: 'cannot-afford',
    label: 'Cannot afford recurring orders',
    icon: Wallet,
  },
  {
    id: 'no-value',
    label: "Don't See the Value in the Membership",
    icon: PackageX,
  },
  {
    id: 'other',
    label: 'Other',
    icon: null,
  },
] as const;

// -- Styled Components --

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #fff;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eee;
`;

const LogoPlaceholder = styled.div`
  width: 140px;
  height: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  gap: 32px;
  padding: 24px 16px;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #000;
  margin: 0;
  text-align: center;
`;

const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const ReasonCard = styled.button<{ selected: boolean; fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px;
  background: #fff;
  border: 1px solid ${({ selected }) => (selected ? '#292929' : '#D1D1D1')};
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
  grid-column: ${({ fullWidth }) => (fullWidth ? '1 / -1' : 'auto')};

  &:hover {
    border-color: #292929;
  }
`;

const ReasonIcon = styled.span<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ selected }) => (selected ? '#f5f0ee' : '#fafafa')};
  color: ${({ selected }) => (selected ? '#292929' : '#767676')};
  transition: background 0.15s, color 0.15s;
`;

const ReasonLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #292929;
  text-align: center;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContinueButton = styled.button<{ isActive: boolean }>`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'not-allowed')};
  background: ${({ isActive }) => (isActive ? '#292929' : '#EFEFEF')};
  border: none;
  color: ${({ isActive }) => (isActive ? '#fff' : '#A6A6A6')};
  transition: opacity 0.15s, background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ReturnButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  background: #fff;
  border: 1px solid #292929;
  color: #292929;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

// -- Component --

interface CancellationReasonPageProps {
  readonly isLoading: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onReturn: () => void;
}

export function CancellationReasonPage({
  isLoading,
  onConfirm,
  onReturn,
}: CancellationReasonPageProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const canSubmit = selectedReason !== null && !isLoading;

  return (
    <PageContainer>
      <HeaderBar>
        <CloseButton
          onClick={onReturn}
          aria-label="Close"
          style={{ opacity: 0, pointerEvents: 'none' }}
        >
          <XIcon size={24} />
        </CloseButton>
        <LogoPlaceholder aria-hidden="true" />
        <CloseButton onClick={onReturn} aria-label="Close">
          <XIcon size={24} />
        </CloseButton>
      </HeaderBar>

      <ContentArea>
        <TopSection>
          <Title>
            Sorry to hear that! We want to make things better for you.
            What&apos;s leading you to consider canceling?
          </Title>

          <ReasonGrid role="radiogroup" aria-label="Cancellation reason">
            {CANCELLATION_REASONS.map((reason) => (
              <ReasonCard
                key={reason.id}
                type="button"
                role="radio"
                aria-checked={selectedReason === reason.label}
                selected={selectedReason === reason.label}
                fullWidth={reason.icon === null}
                onClick={() => setSelectedReason(reason.label)}
                data-testid={`reason-${reason.id}`}
              >
                {reason.icon && (
                  <ReasonIcon selected={selectedReason === reason.label}>
                    <reason.icon size={28} strokeWidth={1.5} />
                  </ReasonIcon>
                )}
                <ReasonLabel>{reason.label}</ReasonLabel>
              </ReasonCard>
            ))}
          </ReasonGrid>
        </TopSection>

        <BottomSection>
          <ContinueButton
            type="button"
            isActive={canSubmit}
            disabled={!canSubmit}
            onClick={() => {
              if (selectedReason) onConfirm(selectedReason);
            }}
          >
            {isLoading ? 'Canceling...' : 'continue to cancel'}
          </ContinueButton>
          <ReturnButton type="button" onClick={onReturn}>
            Return to My account
          </ReturnButton>
        </BottomSection>
      </ContentArea>
    </PageContainer>
  );
}
